import React, { ReactElement, useEffect, useRef, useState } from "react";
import {Link} from "react-router-dom";
import {motion} from "framer-motion";
import {MotionUtils} from "app/utils/motionUtils";
import {UserMenuModel} from "app/model/ua/user-menu";
import ArcButton, {ButtonType} from "app/components/arc/ArcButton";

const scroll = {
  init: {
    clipPath: 'inset(0 0 100% 0)',
    opacity: 0,
    pointerEvents: 'none' as const,
  },
  down: {
    clipPath: 'inset(0 0 0% 0)',
    opacity: 1,
    pointerEvents: 'auto' as const,
    transition: { duration: 0.12, ease: 'easeInOut' },
  },
  up: {
    clipPath: 'inset(0 0 100% 0)',
    opacity: 0,
    pointerEvents: 'none' as const,
    transition: { duration: 0.1, ease: 'easeInOut' },
  },
}

const menuContentFade = {
  init: { opacity: 0 },
  in: {
    opacity: 1,
    transition: { duration: 0.12, delay: 0.04, ease: 'easeInOut' },
  },
  out: {
    opacity: 0,
    transition: { duration: 0.08, delay: 0, ease: 'easeInOut' },
  },
}

const descVariants = {
  init: { opacity: 0, y: 10 },
  in: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, delay: 0.04, ease: 'easeOut' },
  },
  out: {
    opacity: 0,
    y: 6,
    transition: { duration: 0.08, ease: 'easeIn' },
  },
}
export interface MegaMenuOverlayProps {
  ref?: React.RefObject<HTMLDivElement>;
  visible?: boolean;
}

export interface MegaMenuItemProps {
  menu: UserMenuModel;
  activeMenu?: UserMenuModel | undefined;
  currentMenu?: UserMenuModel | undefined;
  isOpen?: boolean;
  showMenuDesc?: boolean;
  /** 바로 연다(키보드 포커스) */
  onOpen?: () => void;
  /** 바로 닫는다(이동, Esc, 포커스가 떠남) */
  onClose?: () => void;
  /** 마우스가 들어옴/나감 — 바로 바꾸지 않고 잠깐 기다린다(MegaMenu 참고) */
  onEnter?: () => void;
  onLeave?: () => void;
  onClick?: (selectedMenu: UserMenuModel, selectedMenusRootMenu: UserMenuModel) => void;
}

/** 주소가 있는 첫 하위 메뉴. 주소 없는 묶음 메뉴를 눌렀을 때 그리로 간다 */
function firstWithUrl(m: UserMenuModel): UserMenuModel | undefined {
  if (m.menuUrlAddr) return m;
  for (const c of m.subMenus ?? []) {
    const found = firstWithUrl(c);
    if (found) return found;
  }
  return undefined;
}

export interface MegaMenuProps {
  visible?: boolean;
  menus?: UserMenuModel[] | undefined | null
  activeMenu?: UserMenuModel | undefined
  currentMenu?: UserMenuModel | undefined
  showMenuDesc?: boolean;
  onClick?: (selectedMenu: UserMenuModel, selectedMenusRootMenu: UserMenuModel) => void;
}

function MegaMenuOverlay(props: Readonly<MegaMenuOverlayProps>): ReactElement {
  const {ref, visible = props.visible ?? false } = props;
  return (
    <motion.div
      variants={MotionUtils.overlay}
      initial="init"
      animate={visible ? 'in' : 'out'}
      transition={{duration: 0.06, ease: 'easeInOut'}}
      className="overlay"
      ref={ref}>
    </motion.div>
  );
}

function MegaMenuItem(props: Readonly<MegaMenuItemProps>): ReactElement {
  const {
    menu,
    activeMenu,
    currentMenu,
    isOpen = false,
    showMenuDesc = true,
    onOpen,
    onClose,
    onEnter,
    onLeave,
    onClick
  } = props
  const itemRef = useRef<HTMLLIElement>(null);

  const hasChildrenMenus: boolean = (menu?.subMenus ?? []).length > 0;
  const [hoveredDesc, setHoveredDesc] = useState<string | null>(null);

  useEffect(() => {
    if (!showMenuDesc) {
      setHoveredDesc(null);
      return;
    }
    if (isOpen) {
      setHoveredDesc(menu?.menuExpl ?? null);
    } else {
      setHoveredDesc(null);
    }
  }, [isOpen, menu?.menuExpl, showMenuDesc]);

  /**
   * 하위 메뉴 클릭. 주소 없는 묶음(예: 분석)을 누르면 그 아래 첫 화면으로 간다 —
   * 예전에는 아무 데도 가지 않고 메뉴만 닫혔다.
   */
  function handleClickMenu(clickedMenu: UserMenuModel, rootMenu?: UserMenuModel) {
    const target = clickedMenu.menuUrlAddr ? clickedMenu : (firstWithUrl(clickedMenu) ?? clickedMenu);
    if (target && onClick) {
      onClick(target, rootMenu ?? clickedMenu);
    }
    onClose?.();
  }

  /**
   * 상단 항목 클릭. 하위가 있으면 연다(닫지 않는다). 예전에는 닫기만 해서, 마우스가
   * 그대로 있는 동안 다시 열 방법이 없었고 터치 화면에서는 열리자마자 닫혔다.
   * 여닫기로 하지 않는 이유: 마우스로 누르면 포커스가 먼저 메뉴를 열어, 이어지는
   * 클릭이 다시 닫아 버린다. 닫기는 메뉴 밖으로 나가기, Esc, 바깥 누르기로 한다.
   */
  function handleClickTop() {
    if (hasChildrenMenus && !menu.menuUrlAddr) {
      onOpen?.();
    } else {
      handleClickMenu(menu);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLLIElement>) {
    if (e.key === "Escape" && isOpen) {
      e.stopPropagation();
      onClose?.();
      itemRef.current?.querySelector<HTMLElement>("button, a")?.focus();
    }
  }

  function handleOpenMenu() {
    if (hasChildrenMenus) {
      onOpen?.();
    }
  }

  function handleBlurMenu(e: React.FocusEvent<HTMLLIElement>) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onClose?.();
    }
  }

  return (
    <>
      {menu && (
        <li
          ref={itemRef}
          className={(activeMenu?.menuId === menu.menuId || isOpen) ? "on" : ""}
          onMouseEnter={() => (hasChildrenMenus ? onEnter : onLeave)?.()}
          onFocus={handleOpenMenu}
          onMouseLeave={() => onLeave?.()}
          onBlur={handleBlurMenu}
          onKeyDown={handleKeyDown}>
          <ArcButton
            type={ButtonType.custom}
            isExportAnchorTag={false}
            text={menu.menuNm}
            onClick={handleClickTop}
          />
          {menu.subMenus && menu.subMenus.length > 0 && (
            <motion.div
              variants={scroll}
              initial="init"
              animate={isOpen ? 'down' : 'up'}
              className="depth-wrap"
              onMouseEnter={() => onEnter?.()}
            >
              <div className="depth-tit">
                <span>{menu?.menuNm}</span>
                {showMenuDesc && hoveredDesc && isOpen && (
                  <motion.p
                    key={hoveredDesc}
                    className="depth-desc"
                    variants={descVariants}
                    initial="init"
                    animate="in"
                  >
                    {hoveredDesc}
                  </motion.p>
                )}
              </div>
              <motion.div
                  variants={menuContentFade}
                  initial="init"
                  animate={isOpen ? 'in' : 'out'}
                  className="w-1600">
                <ul className="depth02-w">
                  {menu?.subMenus?.map((i: UserMenuModel) => {
                    const isDepth02Active: boolean =
                      currentMenu?.menuId === i.menuId ||
                      (i.subMenus?.some((sub: UserMenuModel) => sub.menuId === currentMenu?.menuId) ?? false);
                    return (
                      <li
                        key={i.menuId}
                        className={isDepth02Active ? 'active' : ''}
                        onMouseEnter={showMenuDesc ? () => setHoveredDesc(i.menuExpl ?? menu?.menuExpl ?? null) : undefined}
                        onMouseLeave={showMenuDesc ? () => setHoveredDesc(menu?.menuExpl ?? null) : undefined}
                      >
                        <Link to="#" onClick={(e: any) => { e.preventDefault(); handleClickMenu(i, menu); }}><span>{i.menuNm}</span></Link>
                        <ul className="depth03-w">
                          {i.subMenus?.map((sub: UserMenuModel) => {
                            return (
                              <li key={sub.menuId} className={currentMenu?.menuId === sub.menuId ? 'active' : ''}>
                                <Link to="#" onClick={(e: any) => { e.preventDefault(); handleClickMenu(sub, menu); }}><span>{sub.menuNm}</span></Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            </motion.div>
          )}
        </li>
      )}
    </>
  );
}

export default function MegaMenu(props: MegaMenuProps) {
  const {
    menus,
    visible = props.visible ?? true,
    activeMenu,
    currentMenu,
    showMenuDesc = true,
    onClick
  } = props;

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  /*
   * 마우스 의도 판단.
   *
   * 예전에는 상단 항목에 들어오는 순간 그 메뉴로 바뀌고 나가는 순간 닫혔다. 그래서
   * "탄소 관리" 에서 오른쪽 아래 "배출계수 조회" 로 대각선으로 가면 장외거래, 외부사업
   * 위를 스치며 메뉴가 바뀌다가 닫혔다(실측). 이제
   *   - 처음 열 때는 잠깐(OPEN_DELAY) 머물러야 연다 — 헤더를 지나가기만 해도 열리지 않게
   *   - 열린 상태에서 다른 항목으로 바꿀 때는 SWITCH_DELAY 를 기다린다. 그사이 열린
   *     패널로 들어가면 바꾸지 않는다
   *   - 메뉴 밖으로 나가도 CLOSE_DELAY 만큼 여유를 둔다
   */
  const OPEN_DELAY = 80;
  const SWITCH_DELAY = 150;
  const CLOSE_DELAY = 200;
  const openRef = useRef<string | null>(null);
  openRef.current = openMenuId;
  const timer = useRef<number | undefined>(undefined);
  const clearTimer = () => window.clearTimeout(timer.current);
  const later = (fn: () => void, ms: number) => { clearTimer(); timer.current = window.setTimeout(fn, ms); };
  useEffect(() => clearTimer, []);

  const enter = (id: string | null) => {
    if (openRef.current === id) { clearTimer(); return; }  // 열린 메뉴(또는 그 패널)로 돌아옴
    later(() => setOpenMenuId(id), openRef.current ? SWITCH_DELAY : OPEN_DELAY);
  };
  const leave = () => later(() => setOpenMenuId(null), CLOSE_DELAY);
  const openNow = (id: string | null) => { clearTimer(); setOpenMenuId(id); };
  const closeNow = () => { clearTimer(); setOpenMenuId(null); };

  // 바깥을 누르면 닫는다. 터치 화면에는 '마우스가 나감' 이 없다.
  const navRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (!openMenuId) return;
    const down = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) closeNow();
    };
    document.addEventListener("pointerdown", down);
    return () => document.removeEventListener("pointerdown", down);
  }, [openMenuId]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {visible && menus && menus.length > 0 && (
        <>
          <MegaMenuOverlay visible={openMenuId !== null} />
          <ul ref={navRef}>
            {menus.map((menu: UserMenuModel) => (
              <MegaMenuItem
                key={menu.menuId}
                menu={menu}
                activeMenu={activeMenu}
                currentMenu={currentMenu}
                isOpen={openMenuId === menu.menuId}
                showMenuDesc={showMenuDesc}
                onOpen={() => openNow(menu.menuId ?? null)}
                onClose={closeNow}
                onEnter={() => enter(menu.menuId ?? null)}
                onLeave={leave}
                onClick={onClick}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}
